import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, Button, Grid, Divider,
  Tab, Tabs, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert,
  LinearProgress, Breadcrumbs, Link, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { Edit, ArrowBack, Add, CheckCircle } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { obligationsApi } from '../../services/api';
import type { Obligation, SLAMeasurement } from '../../types';

function TabPanel({ value, index, children }: any) {
  return value === index ? <Box pt={2}>{children}</Box> : null;
}

export default function ObligationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [obligation, setObligation] = useState<Obligation | null>(null);
  const [measurements, setMeasurements] = useState<SLAMeasurement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [measureDialog, setMeasureDialog] = useState(false);
  const [completeDialog, setCompleteDialog] = useState(false);
  const [measureForm, setMeasureForm] = useState({ measurement_date: new Date().toISOString().split('T')[0], measured_value: '', notes: '' });
  const [completionNotes, setCompletionNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await obligationsApi.get(id);
      setObligation(res.data);
      setMeasurements(res.data.measurements || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleAddMeasurement = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await obligationsApi.addMeasurement(id, { ...measureForm, measured_value: parseFloat(measureForm.measured_value) });
      setMeasureDialog(false);
      load();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to add measurement');
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await obligationsApi.update(id, { status: 'completed', completion_notes: completionNotes });
      setCompleteDialog(false);
      load();
    } catch {
      alert('Failed to complete obligation');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d?: string) => d ? format(parseISO(d), 'MMM d, yyyy') : '-';

  if (loading) return <LinearProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!obligation) return null;

  const statusColor: Record<string, any> = {
    completed: 'success', overdue: 'error', at_risk: 'warning', in_progress: 'info', pending: 'default', waived: 'default'
  };

  const complianceRate = measurements.length > 0
    ? Math.round((measurements.filter(m => m.is_compliant).length / measurements.length) * 100)
    : null;

  return (
    <Box>
      <Box mb={3}>
        <Breadcrumbs sx={{ mb: 1 }}>
          <Link underline="hover" color="inherit" sx={{ cursor: 'pointer' }} onClick={() => navigate('/obligations')}>Obligations</Link>
          <Typography color="text.primary" noWrap>{obligation.obligation_name}</Typography>
        </Breadcrumbs>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <Typography variant="h5">{obligation.obligation_name}</Typography>
              <Chip label={obligation.status.replace('_', ' ')} color={statusColor[obligation.status] || 'default'} size="small" sx={{ textTransform: 'capitalize' }} />
              <Chip label={obligation.obligation_type} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {obligation.contract_name} · {obligation.counterparty_name}
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            {obligation.status !== 'completed' && obligation.status !== 'waived' && (
              <Button startIcon={<CheckCircle />} variant="outlined" color="success" onClick={() => setCompleteDialog(true)}>
                Mark Complete
              </Button>
            )}
            {obligation.obligation_type === 'sla' && (
              <Button startIcon={<Add />} variant="outlined" onClick={() => setMeasureDialog(true)}>
                Add Measurement
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* SLA Compliance Banner */}
      {obligation.obligation_type === 'sla' && complianceRate !== null && (
        <Alert severity={complianceRate >= 90 ? 'success' : complianceRate >= 70 ? 'warning' : 'error'} sx={{ mb: 2 }}>
          SLA Compliance Rate: {complianceRate}% ({measurements.filter(m => m.is_compliant).length} / {measurements.length} measurements)
        </Alert>
      )}

      {/* Key Metrics */}
      <Grid container spacing={2} mb={2}>
        {[
          { label: 'Due Date', value: formatDate(obligation.due_date) },
          { label: 'Recurrence', value: obligation.recurrence_type || 'None' },
          { label: 'Assigned To', value: obligation.assigned_name },
          { label: 'SLA Target', value: obligation.sla_target_value != null ? `${obligation.sla_target_value} ${obligation.sla_unit || ''}` : undefined },
          { label: 'Current Value', value: obligation.current_value != null ? `${obligation.current_value} ${obligation.sla_unit || ''}` : undefined },
          { label: 'Last Measured', value: formatDate(obligation.last_measured_at) }
        ].filter(i => i.value).map(item => (
          <Grid item xs={6} sm={4} md={2} key={item.label}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                <Typography variant="body2" fontWeight={600}>{item.value || '-'}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label="Details" />
          {obligation.obligation_type === 'sla' && <Tab label={`Measurements (${measurements.length})`} />}
        </Tabs>

        <CardContent>
          <TabPanel value={tab} index={0}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Description</Typography>
                <Typography variant="body2" color="text.secondary">{obligation.description}</Typography>
              </Grid>
              {obligation.sla_penalty_clause && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>SLA Penalty Clause</Typography>
                  <Typography variant="body2" color="text.secondary">{obligation.sla_penalty_clause}</Typography>
                </Grid>
              )}
              {obligation.notes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Notes</Typography>
                  <Typography variant="body2" color="text.secondary">{obligation.notes}</Typography>
                </Grid>
              )}
              {obligation.completion_notes && (
                <Grid item xs={12}>
                  <Alert severity="success">
                    <Typography variant="subtitle2">Completion Notes</Typography>
                    <Typography variant="body2">{obligation.completion_notes}</Typography>
                    {obligation.completed_at && <Typography variant="caption">Completed: {formatDate(obligation.completed_at)}</Typography>}
                  </Alert>
                </Grid>
              )}
            </Grid>
          </TabPanel>

          <TabPanel value={tab} index={1}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Measured Value</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Target</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Compliant</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Recorded By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {measurements.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell>{formatDate(m.measurement_date)}</TableCell>
                      <TableCell>{m.measured_value} {obligation.sla_unit}</TableCell>
                      <TableCell>{m.target_value} {obligation.sla_unit}</TableCell>
                      <TableCell>
                        <Chip label={m.is_compliant ? 'Yes' : 'No'} color={m.is_compliant ? 'success' : 'error'} size="small" />
                      </TableCell>
                      <TableCell>{m.notes || '-'}</TableCell>
                      <TableCell>{m.recorded_by_name || '-'}</TableCell>
                    </TableRow>
                  ))}
                  {measurements.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">No measurements recorded</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        </CardContent>
      </Card>

      {/* Add Measurement Dialog */}
      <Dialog open={measureDialog} onClose={() => setMeasureDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Record SLA Measurement</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Measurement Date" type="date" value={measureForm.measurement_date}
                onChange={e => setMeasureForm(f => ({ ...f, measurement_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth required label={`Measured Value (${obligation.sla_unit || 'value'})`}
                type="number" value={measureForm.measured_value}
                onChange={e => setMeasureForm(f => ({ ...f, measured_value: e.target.value }))}
                helperText={`Target: ${obligation.sla_target_value} ${obligation.sla_unit}`}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Notes" value={measureForm.notes}
                onChange={e => setMeasureForm(f => ({ ...f, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setMeasureDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddMeasurement} disabled={saving || !measureForm.measured_value}>
            {saving ? 'Saving...' : 'Record Measurement'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete Dialog */}
      <Dialog open={completeDialog} onClose={() => setCompleteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Mark as Complete</DialogTitle>
        <DialogContent>
          <TextField fullWidth multiline rows={3} label="Completion Notes (optional)"
            value={completionNotes} onChange={e => setCompletionNotes(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setCompleteDialog(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleComplete} disabled={saving}>
            {saving ? 'Saving...' : 'Complete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
